import { creatureSprite } from "../art/creatures.ts";
import { enterAge, settleT } from "../art/craftFeel.ts";
import { TYPE_COL, UI, withA } from "../art/pal.ts";
import { SPECIES, type SpeciesId } from "../data/species.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Scene } from "../game/scenes.ts";
import { backdrop, focus, glass, hint, meter, titleBar, typeBadge } from "../ui/kit.ts";
import { BRASS, span } from "./crownHouse.ts";

function clock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * The Raid Gate (Neon Harbor): the current world boss, its shared health pool, the raiders so far
 * and a button to jump in. The fight itself is a server-resolved battle; every scout's hits drain
 * the same pool, and everyone who landed one is paid when it falls.
 */
export function raidScene(ctx: Ctx): Scene {
  let age = 0;
  let poll = 0;
  let asked = 0;
  let toast: { text: string; at: number } | null = null;
  let unsub: () => void = () => {};
  /** The rental picker: six species dealt by the gate, three to choose. */
  let picking: { bossId: string; species: string[]; pick: number; chosen: number[]; i: number } | null = null;
  let wantKit = false;

  return {
    id: "raid",
    native: true,
    enter() {
      age = enterAge(ctx.shot);
      poll = 0;
      toast = null;
      picking = null;
      wantKit = false;
      unsub = ctx.net.on((m) => {
        // While a fight is open over the gate, notices belong to the fight and the street, not here.
        if (m.t === "notice" && !ctx.net.duel) {
          toast = { text: m.text, at: ctx.now };
          ctx.net.notices = ctx.net.notices.filter((n) => n !== m.text);
        }
        if (m.t === "raid") asked = ctx.now;
        if (m.t === "raidKit" && wantKit) {
          wantKit = false;
          picking = { bossId: m.kit.bossId, species: m.kit.species, pick: m.kit.pick, chosen: [], i: 0 };
        }
        // The deal belongs to one boss: if another takes the gate, the picker closes.
        if (m.t === "raid" && picking && m.view.boss?.id !== picking.bossId) {
          picking = null;
          toast = { text: m.view.boss ? "A new boss is at the gate. Pick your rentals again." : "The boss is gone. Wait for the next one.", at: ctx.now };
        }
      });
      if (ctx.net.live) ctx.net.send({ t: "raid" });
    },
    exit() {
      unsub();
    },
    update() {
      age += ctx.dt;
      poll += ctx.dt;
      if (ctx.net.live && poll > 2500) {
        poll = 0;
        ctx.net.send({ t: "raid" });
      }
      if (picking) {
        const p = picking;
        const d = ctx.input.menuDir(ctx.now);
        if (d) {
          const col = p.i % 3, row = Math.floor(p.i / 3);
          p.i = d.x ? row * 3 + ((col + d.x + 3) % 3) : ((row + d.y + 2) % 2) * 3 + col;
          p.i = Math.min(p.i, p.species.length - 1);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          if (p.chosen.length) p.chosen.pop();
          else picking = null;
          return;
        }
        if (!ctx.input.consume("Enter")) return;
        const at = p.chosen.indexOf(p.i);
        if (at >= 0) p.chosen.splice(at, 1);
        else if (p.chosen.length < p.pick) p.chosen.push(p.i);
        ctx.audio.confirm();
        if (p.chosen.length === p.pick) {
          ctx.net.send({ t: "raidJoin", picks: p.chosen, bossId: p.bossId });
          picking = null;
        }
        return;
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (!ctx.input.consume("Enter")) return;
      if (!ctx.net.live) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (!ctx.net.raid?.boss) {
        ctx.audio.fail();
        toast = { text: "Nothing stirs yet. Come back when the gate glows.", at: ctx.now };
        return;
      }
      ctx.audio.confirm();
      // The gate lends everyone the same strength: pick three of six rentals, then join.
      wantKit = true;
      ctx.net.send({ t: "raidKit" });
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const t = settleT(age, reduced, 120);
      backdrop(g, ctx.now, reduced, [40, 26, 96, 255], [8, 6, 22, 255]);
      const crownFight = !!ctx.net.raid?.boss?.crown;
      titleBar(g, crownFight ? "RAID GATE · CROWN RAID" : "RAID GATE", ctx.net.live ? `${ctx.net.online || 1} online` : "offline", crownFight ? BRASS : [170, 130, 255, 255]);
      if (!ctx.net.live) {
        glass(g, 56, 70, 272, 80, { r: 10 });
        g.textShadow(70, 82, "The gate is dark offline.", UI.text, UI.ink);
        let y = 98;
        for (const row of wrapPx("World bosses live on the server. Connect to the live world to raid with other scouts.", 244)) {
          g.text(70, y, row, UI.textDim);
          y += 11;
        }
        hint(g, 70, 134, "A", "back", "a");
        return;
      }
      const v = ctx.net.raid;
      const b = v?.boss;
      const lx = 8 - Math.round((1 - t) * 30);
      glass(g, lx, 28, 200, 152, { r: 10, border: b ? UI.pink : UI.line });
      if (b && v?.pool) {
        const id = b.species as SpeciesId;
        const sp = SPECIES[id];
        const pulse = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(ctx.now / 300);
        g.disc(lx + 100, 92, 44 + pulse * 3, withA(TYPE_COL[sp.types[0]!]!.fill, 60));
        g.disc(lx + 100, 92, 34, withA(UI.ink, 120));
        g.blit(creatureSprite(id, "front", 3), lx + 52, 44);
        g.textShadow(lx + 10, 34, `${b.crown ? "★ " : ""}${b.name.toUpperCase()}  Lv${b.level}`, b.crown ? UI.goldHi : UI.text, UI.ink);
        let bx = lx + 10;
        for (const type of sp.types) bx += typeBadge(g, bx, 46, type, true) + 2;
        const frac = v.pool.max ? v.pool.hp / v.pool.max : 0;
        g.text(lx + 10, 146, "POOL", UI.textDim);
        meter(g, lx + 36, 147, 154, 7, frac, { tone: { fill: UI.pink, hi: [255, 200, 240, 255], lo: [150, 40, 120, 255] } });
        g.text(lx + 10, 160, `${v.pool.hp.toLocaleString("en-US")} / ${v.pool.max.toLocaleString("en-US")}`, UI.text);
        g.textR(lx + 192, 160, `${v.pool.fighters} fighting  ${clock(v.endsIn - (ctx.now - asked))}`, UI.textDim);
      } else {
        g.textShadow(lx + 10, 34, "THE GATE IS QUIET", UI.text, UI.ink);
        let y = 60;
        for (const row of wrapPx("No boss right now. The next one wakes soon; every scout online is told when it does.", 180)) {
          g.text(lx + 10, y, row, UI.textDim);
          y += 11;
        }
        if (v) g.textShadow(lx + 10, 110, `Next boss in ${clock(v.nextIn - (ctx.now - asked))}`, UI.goldHi, UI.ink);
      }
      if (v?.crownIn !== undefined && !b?.crown) {
        // The Crown Raid countdown: a siren for the last ten minutes.
        const left = v.crownIn - (ctx.now - asked);
        const blink = v.siren && !reduced && Math.floor(ctx.now / 400) % 2 === 0;
        g.rrect(lx + 8, 128, 184, 14, 5, withA(v.siren ? UI.pink : BRASS, blink ? 120 : 70));
        g.textShadow(lx + 14, 131, v.siren ? `CROWN SIREN: a legend in ${clock(left)}` : `Next Crown Raid in ${span(left)}`, UI.text, UI.ink);
      }

      const rx = 214 + Math.round((1 - t) * 30);
      glass(g, rx, 28, 162, 90, { r: 10 });
      g.textShadow(rx + 10, 34, "TOP RAIDERS", UI.textDim, UI.ink);
      const top = v?.top ?? [];
      if (!top.length) g.text(rx + 10, 50, "No hits yet. Be first!", UI.textMute);
      top.forEach((r, i) => {
        g.text(rx + 10, 50 + i * 12, `${i + 1}. ${fitText(r.name, 80)}`, i === 0 ? UI.goldHi : UI.text);
        g.textR(rx + 152, 50 + i * 12, `${r.dmg}`, UI.textDim);
      });
      glass(g, rx, 122, 162, 58, { r: 10 });
      g.textShadow(rx + 10, 128, "YOUR DAMAGE", UI.textDim, UI.ink);
      g.textBig(rx + 10, 142, `${v?.mine ?? 0}`, UI.goldHi, 2);
      g.text(rx + 10 + measureText(`${v?.mine ?? 0}`, 2) + 6, 150, "HP", UI.textDim);
      const econ = ctx.net.econ?.mode;
      const pts = v?.potPoints ?? 0;
      const unregistered = (econ === "live" || econ === "shadow") && !ctx.net.econ?.wallet.registered;
      const line = unregistered ? "Register at the Crown House to earn Brass."
        : b?.crown ? "Its Crown sells; raiders split it." : pts ? `${pts} pts in this hour's pot`
          : econ === "live" || econ === "shadow" ? "Hits earn Brass from the pot." : "Top raider recruits the boss.";
      g.text(rx + 10, 166, fitText(line, 146), pts && !unregistered ? BRASS : UI.textMute);
      hint(g, 8, SCREEN_H - 30, "A", b ? "pick rentals" : "check again", "a");
      hint(g, 8, SCREEN_H - 16, "B", "back to the harbor", "b");
      g.text(150, SCREEN_H - 28, "The gate lends every scout Lv40 rentals:", UI.textMute);
      g.text(150, SCREEN_H - 17, "your crew stays home, every hit counts.", UI.textMute);
      if (picking) drawPicker(picking);
      if (toast && ctx.now - toast.at < 3500) {
        const w = Math.min(360, measureText(toast.text) + 20);
        glass(g, (SCREEN_W - w) / 2, 8, w, 16, { r: 6, alpha: 235 });
        g.text((SCREEN_W - w) / 2 + 10, 12, fitText(toast.text, w - 20), UI.text);
      }
    },
  };

  function drawPicker(p: NonNullable<typeof picking>): void {
    const g = ctx.g;
    g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, 160]);
    glass(g, 34, 26, 316, 164, { r: 10, border: [170, 130, 255, 255] });
    g.textShadow(46, 34, `Pick ${p.pick} rentals (${p.chosen.length}/${p.pick})`, UI.text, UI.ink);
    g.textR(338, 34, "Lv40, same for everyone", UI.textDim);
    p.species.forEach((id, k) => {
      const sp = SPECIES[id as SpeciesId];
      if (!sp) return;
      const x = 46 + (k % 3) * 100, y = 50 + Math.floor(k / 3) * 64;
      const on = p.i === k;
      const n = p.chosen.indexOf(k);
      g.rrect(x, y, 92, 58, 6, n >= 0 ? withA(UI.teal, 70) : withA(UI.bg1, 200));
      g.blit(creatureSprite(id as SpeciesId, "front", 1), x + 2, y + 2);
      g.text(x + 38, y + 8, fitText(sp.name, 52), UI.text);
      let bx = x + 5;
      for (const type of sp.types) bx += typeBadge(g, bx, y + 42, type, true) + 2;
      if (n >= 0) g.textShadow(x + 82, y + 24, `${n + 1}`, UI.goldHi, UI.ink);
      if (on) focus(g, x - 1, y - 1, 94, 60, ctx.now, ctx.reduced, 7);
    });
    hint(g, 46, 180 - 4, "A", "pick / unpick", "a");
    hint(g, 150, 180 - 4, "B", p.chosen.length ? "undo" : "cancel", "b");
  }
}
